import React, { useEffect, Suspense, lazy } from 'react'
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom'
import { LanguageProvider } from './contexts/LanguageContext'
import { AuthProvider } from './contexts/AuthContext'
import { ToastProvider } from './contexts/ToastContext'
import { ThemeProvider } from './contexts/ThemeContext'
import ErrorBoundary from './components/ui/ErrorBoundary'
import Home from './pages/Home'
import SwiftPOS from './pages/SwiftPOS'
import PrivacyPolicy from './pages/PrivacyPolicy'
import Terms from './pages/Terms'
import NotFound from './pages/NotFound'
import ProtectedRoute from './routes/ProtectedRoute'
import { useAnalytics } from './hooks/useAnalytics'
import { useReveal } from './hooks/useReveal'
import app from './services/firebase'

const Login = lazy(() => import('./pages/admin/Login'))
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'))

const StealthNavigation: React.FC = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl + Shift + A
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        navigate('/v3-x-command-root');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [navigate]);

  return null;
};

const GlobalEffects: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useAnalytics();
  useReveal();
  return (
    <>
      <StealthNavigation />
      {children}
    </>
  );
};

const FirebaseCheck: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  if (!app) {
    return (
      <div style={{
        height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg)', color: 'var(--text)', textAlign: 'center', padding: '20px'
      }}>
        <h1 style={{ color: 'var(--error)' }}>Configuration Required</h1>
        <p style={{ color: 'var(--text-muted)', maxWidth: '500px', marginTop: '10px' }}>
          Firebase is not properly configured. Please check your console for details.
        </p>
      </div>
    );
  }
  return <>{children}</>;
};

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <ToastProvider>
              <Router>
                <GlobalEffects>
                  <FirebaseCheck>
                    <Suspense fallback={
                      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', color: 'var(--text-muted)' }}>
                        Loading...
                      </div>
                    }>
                      <Routes>
                        {/* Public: section anchors use hash routing (#home, #contact);
                            standalone pages use clean paths (/swiftpos, /privacy, /terms).
                            firebase.json rewrites ** -> /index.html, so hard refresh
                            on clean paths does NOT 404 on Firebase Hosting. */}
                        <Route path="/" element={<Home />} />
                        <Route path="/swiftpos" element={<SwiftPOS />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route path="/terms" element={<Terms />} />

                        {/* Obscured Admin Routes */}
                        <Route path="/v3-x-command-root/login" element={<Login />} />
                        <Route path="/v3-x-command-root" element={
                          <ProtectedRoute>
                            <AdminDashboard />
                          </ProtectedRoute>
                        } />

                        {/* Fallback Redirects to prevent discovery */}
                        <Route path="/admin" element={<NotFound />} />
                        <Route path="/login" element={<NotFound />} />
                        <Route path="/admin-dashboardHK" element={<NotFound />} />

                        {/* Fallback 404 */}
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </Suspense>
                  </FirebaseCheck>
                </GlobalEffects>
              </Router>
            </ToastProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </ErrorBoundary>
  )
}

export default App
